import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Building,
  Users,
  Mail,
  Check,
  X,
  Crown,
  User,
  AlertCircle,
  Wifi,
  WifiOff,
  Loader2,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { offlineFirstInvitationService } from "../../services/offlineFirstInvitationService";
import { OrganizationInvitation } from "../../types/organization";
import { TeamInvitation } from "../../services/teamService";

interface OfflineFirstInvitationNotificationsProps {
  onInvitationHandled?: () => void;
}

export function OfflineFirstInvitationNotifications({
  onInvitationHandled,
}: OfflineFirstInvitationNotificationsProps) {
  const [orgInvitations, setOrgInvitations] = useState<OrganizationInvitation[]>([]);
  const [teamInvitations, setTeamInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [needsSync, setNeedsSync] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<{
    type: "organization" | "team";
    invitation: OrganizationInvitation | TeamInvitation;
    action: "accept" | "reject";
  } | null>(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('[OfflineFirstInvitationNotifications] Back online - checking for sync');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[OfflineFirstInvitationNotifications] Gone offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load invitations from IndexedDB (offline-first)
  const loadInvitations = async () => {
    try {
      setLoading(true);
      console.log('[OfflineFirstInvitationNotifications] Loading invitations (offline-first)');

      // Always load from IndexedDB first
      const result = await offlineFirstInvitationService.getPendingInvitations();

      if (result.success) {
        setOrgInvitations(result.organizationInvitations);
        setTeamInvitations(result.teamInvitations);
        setNeedsSync(result.needsSync || false);

        console.log('[OfflineFirstInvitationNotifications] Invitations loaded:', {
          orgCount: result.organizationInvitations.length,
          teamCount: result.teamInvitations.length,
          needsSync: result.needsSync
        });
      } else {
        console.warn('[OfflineFirstInvitationNotifications] Failed to load invitations:', result.error);

        // Only show error toast if we're online (offline errors are expected)
        if (isOnline && result.error) {
          toast({
            title: "Error",
            description: result.error,
            variant: "destructive",
          });
        }

        // Set empty arrays on error
        setOrgInvitations([]);
        setTeamInvitations([]);
      }
    } catch (error) {
      console.error('[OfflineFirstInvitationNotifications] Error loading invitations:', error);

      // Only show error toast if we're online
      if (isOnline) {
        toast({
          title: "Error",
          description: "Failed to load invitations",
          variant: "destructive",
        });
      }

      setOrgInvitations([]);
      setTeamInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  // Load invitations on mount and when coming back online
  useEffect(() => {
    loadInvitations();
  }, []); // Only run once on mount

  // Handle invitation actions (accept/reject)
  const handleAction = async () => {
    if (!selectedInvitation || !selectedInvitation.invitation) return;

    try {
      setActionLoading(selectedInvitation.invitation.id);
      console.log(`[OfflineFirstInvitationNotifications] ${selectedInvitation.action}ing invitation:`, selectedInvitation.invitation.id);

      let result;
      if (selectedInvitation.action === "accept") {
        result = await offlineFirstInvitationService.acceptInvitation(
          selectedInvitation.invitation.id,
          selectedInvitation.type
        );
      } else {
        result = await offlineFirstInvitationService.rejectInvitation(
          selectedInvitation.invitation.id,
          selectedInvitation.type
        );
      }

      if (result.success) {
        const actionText = selectedInvitation.action === "accept" ? "accepted" : "rejected";

        toast({
          title: "Success",
          description: result.needsSync
            ? `Invitation ${actionText}. Will sync when online.`
            : `Invitation ${actionText} successfully`,
        });

        // Update sync status
        if (result.needsSync) {
          setNeedsSync(true);
        }

        // Reload invitations to reflect changes
        await loadInvitations();
        onInvitationHandled?.();
      } else {
        toast({
          title: "Error",
          description: result.error || `Failed to ${selectedInvitation.action} invitation`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`[OfflineFirstInvitationNotifications] Error ${selectedInvitation.action}ing invitation:`, error);
      toast({
        title: "Error",
        description: `Failed to ${selectedInvitation.action} invitation`,
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
      setSelectedInvitation(null);
    }
  };

  const totalInvitations = orgInvitations.length + teamInvitations.length;

  // Don't render if no invitations
  if (totalInvitations === 0) {
    return null;
  }

  return (
    <>
      {/* Confirmation Dialog */}
      <AlertDialog
        open={!!selectedInvitation}
        onOpenChange={() => setSelectedInvitation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              {selectedInvitation?.action === "accept" ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <X className="w-5 h-5 text-red-600" />
              )}
              <span>
                {selectedInvitation?.action === "accept" ? "Accept" : "Reject"}{" "}
                Invitation
              </span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedInvitation?.action === "accept" ? (
                <>
                  Are you sure you want to accept this invitation to join{" "}
                  <strong>
                    {selectedInvitation?.type === "organization"
                      ? (selectedInvitation?.invitation as OrganizationInvitation)?.organization_name
                      : (selectedInvitation?.invitation as TeamInvitation)?.team_name}
                  </strong>
                  ?
                </>
              ) : (
                <>
                  Are you sure you want to reject this invitation to join{" "}
                  <strong>
                    {selectedInvitation?.type === "organization"
                      ? (selectedInvitation?.invitation as OrganizationInvitation)?.organization_name
                      : (selectedInvitation?.invitation as TeamInvitation)?.team_name}
                  </strong>
                  ?
                </>
              )}
              {!isOnline && (
                <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  You're offline. This action will be synced when you're back online.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={
                selectedInvitation?.action === "accept"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {selectedInvitation?.action === "accept" ? "Accept" : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
